import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { createChatTagCategory, updateChatTagCategory } from '../../../hooks/chat/api';
import { ChatTagCategory } from '../tagGlossary';
import { TEXT_MAX, isWholePositionFrom1 } from './TagDialog';

export const NAME_MAX = 100;

interface Props {
  open: boolean;
  /** The category being edited; null for a new one. */
  category: ChatTagCategory | null;
  onClose: () => void;
  onSaved: (what: 'added' | 'saved') => void;
  onError: (error: unknown) => void;
}

interface Draft {
  name: string;
  description: string;
  notes: string;
  position: string;
}

const emptyDraft = (): Draft => ({ name: '', description: '', notes: '', position: '' });

const fromCategory = (category: ChatTagCategory): Draft => ({
  name: category.name,
  description: category.description ?? '',
  notes: category.notes ?? '',
  position: String(category.position),
});

export function categoryDraftProblem(draft: Draft): string | null {
  if (!draft.name.trim()) return 'Give the category a name.';
  if (draft.name.trim().length > NAME_MAX)
    return `The name is at most ${NAME_MAX} characters.`;
  if (draft.position.trim() && !isWholePositionFrom1(draft.position))
    return 'The position is a whole number from 1.';
  return null;
}

const orNull = (text: string): string | null => (text.trim() ? text.trim() : null);

/**
 * Add or edit a rider-support category (a topic: the two markers were
 * seeded and need no new ones; partner support has no tab in phase 1).
 */
export function CategoryDialog({ open, category, onClose, onSaved, onError }: Props) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(category ? fromCategory(category) : emptyDraft());
  }, [open, category]);

  const problem = categoryDraftProblem(draft);

  const save = async () => {
    if (problem) return;
    setSaving(true);
    try {
      const fields = {
        name: draft.name.trim(),
        description: orNull(draft.description),
        notes: orNull(draft.notes),
      };
      if (category) {
        await updateChatTagCategory(category.id, {
          ...fields,
          position: Number(draft.position) || category.position,
        });
      } else {
        await createChatTagCategory({
          section: 'RIDER_SUPPORT',
          ...fields,
          ...(draft.position.trim() ? { position: Number(draft.position) } : {}),
        });
      }
      onSaved(category ? 'saved' : 'added');
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            label="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            inputProps={{ maxLength: NAME_MAX, 'aria-label': 'Name' }}
            helperText={`${draft.name.length}/${NAME_MAX}`}
            size="small"
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX, 'aria-label': 'Description' }}
            multiline
            minRows={2}
            size="small"
          />
          <TextField
            label="Notes"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX, 'aria-label': 'Category notes' }}
            multiline
            minRows={1}
            size="small"
          />
          <TextField
            label="Position"
            value={draft.position}
            onChange={(e) => setDraft({ ...draft, position: e.target.value })}
            inputProps={{ inputMode: 'numeric', 'aria-label': 'Category position' }}
            helperText={category ? 'Order in the list' : 'Empty: last in the list'}
            size="small"
            sx={{ width: 160 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || Boolean(problem)}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {category ? 'Save' : 'Add category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
